/**
 * Firewall Profile Store
 * 
 * Zustand store for managing firewall profiles:
 * - Profile CRUD operations
 * - Device group management
 * - Validation state
 * - Test results
 * 
 * Peer reviewed: Gemini 2.5 Pro, GPT-4o
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  FirewallProfile,
  FirewallRule,
  DeviceGroup,
  ValidationResult,
  ValidationIssue,
  TestPacket,
  TestResult,
  ProfileStats,
  PortRule,
  IcmpRule,
} from '../types/firewall';

// ============================================================================
// State Interface
// ============================================================================

interface FirewallState {
  // Profiles
  profiles: Map<string, FirewallProfile>;
  selectedProfileId: string | null;
  draftProfile: Partial<FirewallProfile> | null;
  
  // Device Groups (with profileId for unidirectional relationship)
  deviceGroups: Map<string, DeviceGroup>;
  
  // Profile Stats (fetched separately)
  profileStats: Map<string, ProfileStats>;
  
  // Validation
  validationResult: ValidationResult | null;
  isValidating: boolean;
  
  // Testing
  testResults: TestResult[];
  isTesting: boolean;
  
  // UI State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface FirewallActions {
  // Profile CRUD
  setProfiles: (profiles: FirewallProfile[]) => void;
  addProfile: (profile: FirewallProfile) => void;
  updateProfile: (id: string, updates: Partial<FirewallProfile>) => void;
  deleteProfile: (id: string) => void;
  duplicateProfile: (id: string, newName: string) => string | null;
  
  // Selection
  selectProfile: (id: string | null) => void;
  
  // Draft Management
  setDraft: (draft: Partial<FirewallProfile> | null) => void;
  updateDraft: (updates: Partial<FirewallProfile>) => void;
  saveDraft: () => void;
  discardDraft: () => void;
  
  // Rule Management
  addRule: (rule: FirewallRule) => void;
  updateRule: (ruleId: string, updates: Partial<FirewallRule>) => void;
  deleteRule: (ruleId: string) => void;
  reorderRules: (fromIndex: number, toIndex: number) => void;
  toggleRule: (ruleId: string) => void;
  
  // Device Groups
  setDeviceGroups: (groups: DeviceGroup[]) => void;
  assignProfileToGroup: (groupId: string, profileId: string | undefined) => void;
  getGroupsForProfile: (profileId: string) => DeviceGroup[];
  
  // Stats
  setProfileStats: (profileId: string, stats: ProfileStats) => void;
  
  // Validation
  setValidationResult: (result: ValidationResult | null) => void;
  validateProfile: () => ValidationResult;
  setIsValidating: (isValidating: boolean) => void;
  
  // Testing
  testPacket: (packet: TestPacket) => TestResult;
  clearTestResults: () => void;
  setIsTesting: (isTesting: boolean) => void;
  
  // Loading States
  setIsLoading: (isLoading: boolean) => void;
  setIsSaving: (isSaving: boolean) => void;
  setError: (error: string | null) => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: FirewallState = {
  profiles: new Map(),
  selectedProfileId: null,
  draftProfile: null,
  deviceGroups: new Map(),
  profileStats: new Map(),
  validationResult: null,
  isValidating: false,
  testResults: [],
  isTesting: false,
  isLoading: false,
  isSaving: false,
  error: null,
};

// ============================================================================
// Validation Helpers
// ============================================================================

function validateProfileRules(profile: Partial<FirewallProfile>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const rules = profile.rules || [];
  
  // Check for empty name
  if (!profile.name?.trim()) {
    issues.push({
      id: 'name-empty',
      severity: 'error',
      field: 'name',
      message: 'Profile name is required',
    });
  }
  
  // Check for duplicate ports
  const portMap = new Map<string, string[]>();
  for (const rule of rules) {
    if (rule.type === 'port') {
      const portRule = rule;
      const key = `${portRule.port}-${portRule.protocol}`;
      const existing = portMap.get(key) || [];
      existing.push(rule.id);
      portMap.set(key, existing);
    }
  }
  
  for (const [key, ruleIds] of portMap) {
    if (ruleIds.length > 1) {
      issues.push({
        id: `duplicate-${key}`,
        severity: 'warning',
        field: 'rules',
        message: `Port ${key} has multiple rules. Only the first enabled rule will apply.`,
        ruleId: ruleIds[0],
      });
    }
  }
  
  // Check redirect targets
  for (const rule of rules) {
    if (rule.type === 'port') {
      const portRule = rule;
      if (portRule.state === 'redirect' && !portRule.redirectTarget) {
        issues.push({
          id: `redirect-missing-${rule.id}`,
          severity: 'error',
          field: 'rules',
          message: `Redirect rule on port ${portRule.port} has no target specified`,
          ruleId: rule.id,
        });
      }
      if (portRule.state === 'redirect' && portRule.redirectTarget) {
        // Validate target format
        if (!portRule.redirectTarget.host.trim()) {
          issues.push({
            id: `redirect-host-${rule.id}`,
            severity: 'error',
            field: 'rules',
            message: `Redirect target host is empty for port ${portRule.port}`,
            ruleId: rule.id,
          });
        }
        if (portRule.redirectTarget.port < 1 || portRule.redirectTarget.port > 65535) {
          issues.push({
            id: `redirect-port-${rule.id}`,
            severity: 'error',
            field: 'rules',
            message: `Redirect target port ${portRule.redirectTarget.port} is invalid (must be 1-65535)`,
            ruleId: rule.id,
          });
        }
      }
      if (portRule.state === 'obfuscate' && !portRule.obfuscationMethod) {
        issues.push({
          id: `obfuscate-missing-${rule.id}`,
          severity: 'error',
          field: 'rules',
          message: `Obfuscate rule on port ${portRule.port} has no method specified`,
          ruleId: rule.id,
        });
      }
    }
  }
  
  // Warn about no rules
  if (rules.length === 0) {
    issues.push({
      id: 'no-rules',
      severity: 'warning',
      field: 'rules',
      message: 'Profile has no rules defined. All traffic will use default behavior.',
    });
  }
  
  return issues;
}

function matchPacketToRule(packet: TestPacket, rules: FirewallRule[]): FirewallRule | undefined {
  for (const rule of rules) {
    if (!rule.enabled) {continue;}
    
    if (packet.protocol === 'icmp') {
      if (rule.type === 'icmp') {
        const icmpRule = rule;
        if (icmpRule.icmpType === packet.icmpType) {
          if (icmpRule.icmpCode === undefined || icmpRule.icmpCode === packet.icmpCode) {
            return rule;
          }
        }
      }
    } else {
      if (rule.type === 'port') {
        const portRule = rule;
        if (portRule.protocol === packet.protocol) {
          const portInRange = portRule.endPort
            ? packet.destinationPort >= portRule.port && packet.destinationPort <= portRule.endPort
            : packet.destinationPort === portRule.port;
          if (portInRange) {
            return rule;
          }
        }
      }
    }
  }
  return undefined;
}

// ============================================================================
// Store Creation
// ============================================================================

export const useFirewallStore = create<FirewallState & FirewallActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // ----------------------------------------------------------------------
        // Profile CRUD
        // ----------------------------------------------------------------------
        
        setProfiles: (profiles: FirewallProfile[]) => {
          set((state) => {
            state.profiles.clear();
            profiles.forEach((p) => state.profiles.set(p.id, p));
          });
        },
        
        addProfile: (profile: FirewallProfile) => {
          set((state) => {
            state.profiles.set(profile.id, profile);
          });
        },
        
        updateProfile: (id: string, updates: Partial<FirewallProfile>) => {
          set((state) => {
            const existing = state.profiles.get(id);
            if (existing) {
              state.profiles.set(id, { ...existing, ...updates, updatedAt: new Date().toISOString() });
            }
          });
        },
        
        deleteProfile: (id: string) => {
          set((state) => {
            state.profiles.delete(id);
            if (state.selectedProfileId === id) {
              state.selectedProfileId = null;
            }
            // Unassign from device groups
            state.deviceGroups.forEach((group) => {
              if (group.profileId === id) {
                group.profileId = undefined;
              }
            });
          });
        },
        
        duplicateProfile: (id: string, newName: string) => {
          const existing = get().profiles.get(id);
          if (!existing) {return null;}
          
          const newId = crypto.randomUUID();
          const duplicate: FirewallProfile = {
            ...existing,
            id: newId,
            name: newName,
            isDefault: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            rules: existing.rules.map((r) => ({ ...r, id: crypto.randomUUID() })),
          };
          
          set((state) => {
            state.profiles.set(newId, duplicate);
          });
          
          return newId;
        },

        // ----------------------------------------------------------------------
        // Selection
        // ----------------------------------------------------------------------
        
        selectProfile: (id: string | null) => {
          set((state) => {
            state.selectedProfileId = id;
            state.validationResult = null;
            state.testResults = [];
          });
        },

        // ----------------------------------------------------------------------
        // Draft Management
        // ----------------------------------------------------------------------
        
        setDraft: (draft: Partial<FirewallProfile> | null) => {
          set((state) => {
            state.draftProfile = draft;
            state.validationResult = null;
          });
        },
        
        updateDraft: (updates: Partial<FirewallProfile>) => {
          set((state) => {
            if (state.draftProfile) {
              state.draftProfile = { ...state.draftProfile, ...updates };
            }
          });
        },
        
        saveDraft: () => {
          const draft = get().draftProfile;
          if (!draft?.id) {return;}
          
          const now = new Date().toISOString();
          const fullProfile: FirewallProfile = {
            id: draft.id,
            name: draft.name || 'Untitled Profile',
            description: draft.description || '',
            isDefault: draft.isDefault || false,
            rules: draft.rules || [],
            createdAt: draft.createdAt || now,
            updatedAt: now,
            createdBy: draft.createdBy || 'current-user',
            isActive: draft.isActive ?? true,
            tags: draft.tags || [],
            extendsProfileId: draft.extendsProfileId,
          };
          
          set((state) => {
            state.profiles.set(fullProfile.id, fullProfile);
            state.selectedProfileId = fullProfile.id;
            state.draftProfile = null;
          });
        },
        
        discardDraft: () => {
          set((state) => {
            state.draftProfile = null;
          });
        },

        // ----------------------------------------------------------------------
        // Rule Management
        // ----------------------------------------------------------------------
        
        addRule: (rule: FirewallRule) => {
          set((state) => {
            if (state.draftProfile) {
              state.draftProfile.rules = [...(state.draftProfile.rules || []), rule];
            }
          });
        },
        
        updateRule: (ruleId: string, updates: Partial<FirewallRule>) => {
          set((state) => {
            if (state.draftProfile?.rules) {
              const index = state.draftProfile.rules.findIndex((r) => r.id === ruleId);
              if (index !== -1) {
                state.draftProfile.rules[index] = { ...state.draftProfile.rules[index], ...updates } as FirewallRule;
              }
            }
          });
        },
        
        deleteRule: (ruleId: string) => {
          set((state) => {
            if (state.draftProfile?.rules) {
              state.draftProfile.rules = state.draftProfile.rules.filter((r) => r.id !== ruleId);
            }
          });
        },
        
        reorderRules: (fromIndex: number, toIndex: number) => {
          set((state) => {
            if (state.draftProfile?.rules) {
              const rules = [...state.draftProfile.rules];
              const [moved] = rules.splice(fromIndex, 1);
              if (moved) {
                rules.splice(toIndex, 0, moved);
                state.draftProfile.rules = rules;
              }
            }
          });
        },
        
        toggleRule: (ruleId: string) => {
          set((state) => {
            if (state.draftProfile?.rules) {
              const rule = state.draftProfile.rules.find((r) => r.id === ruleId);
              if (rule) {
                rule.enabled = !rule.enabled;
              }
            }
          });
        },

        // ----------------------------------------------------------------------
        // Device Groups
        // ----------------------------------------------------------------------
        
        setDeviceGroups: (groups: DeviceGroup[]) => {
          set((state) => {
            state.deviceGroups.clear();
            groups.forEach((g) => state.deviceGroups.set(g.id, g));
          });
        },
        
        assignProfileToGroup: (groupId: string, profileId: string | undefined) => {
          set((state) => {
            const group = state.deviceGroups.get(groupId);
            if (group) {
              group.profileId = profileId;
            }
          });
        },
        
        getGroupsForProfile: (profileId: string) => {
          return Array.from(get().deviceGroups.values()).filter((g) => g.profileId === profileId);
        },

        // ----------------------------------------------------------------------
        // Stats
        // ----------------------------------------------------------------------
        
        setProfileStats: (profileId: string, stats: ProfileStats) => {
          set((state) => {
            state.profileStats.set(profileId, stats);
          });
        },

        // ----------------------------------------------------------------------
        // Validation
        // ----------------------------------------------------------------------
        
        setValidationResult: (result: ValidationResult | null) => {
          set((state) => {
            state.validationResult = result;
          });
        },
        
        validateProfile: () => {
          const draft = get().draftProfile;
          const issues = validateProfileRules(draft || {});
          const result: ValidationResult = {
            isValid: !issues.some((i) => i.severity === 'error'),
            issues,
            testedAt: new Date().toISOString(),
          };
          set((state) => {
            state.validationResult = result;
          });
          return result;
        },
        
        setIsValidating: (isValidating: boolean) => {
          set((state) => {
            state.isValidating = isValidating;
          });
        },

        // ----------------------------------------------------------------------
        // Testing
        // ----------------------------------------------------------------------
        
        testPacket: (packet: TestPacket) => {
          const draft = get().draftProfile;
          const rules = draft?.rules || [];
          const matchedRule = matchPacketToRule(packet, rules);
          
          let action: TestResult['action'] = 'allowed';
          let redirectTarget: TestResult['redirectTarget'];
          
          if (matchedRule) {
            if (matchedRule.type === 'port') {
              const portRule = matchedRule;
              switch (portRule.state) {
                case 'closed':
                  action = 'blocked';
                  break;
                case 'redirect':
                  action = 'redirected';
                  redirectTarget = portRule.redirectTarget;
                  break;
                case 'obfuscate':
                  action = 'obfuscated';
                  break;
                default:
                  action = 'allowed';
              }
            } else {
              const icmpRule = matchedRule;
              action = icmpRule.state === 'open' ? 'allowed' : 'blocked';
            }
          }
          
          const result: TestResult = {
            packet,
            matchedRule,
            action,
            redirectTarget,
            timestamp: new Date().toISOString(),
          };
          
          set((state) => {
            state.testResults.push(result);
          });
          
          return result;
        },
        
        clearTestResults: () => {
          set((state) => {
            state.testResults = [];
          });
        },
        
        setIsTesting: (isTesting: boolean) => {
          set((state) => {
            state.isTesting = isTesting;
          });
        },

        // ----------------------------------------------------------------------
        // Loading States
        // ----------------------------------------------------------------------
        
        setIsLoading: (isLoading: boolean) => {
          set((state) => {
            state.isLoading = isLoading;
          });
        },
        
        setIsSaving: (isSaving: boolean) => {
          set((state) => {
            state.isSaving = isSaving;
          });
        },
        
        setError: (error: string | null) => {
          set((state) => {
            state.error = error;
            state.isLoading = false;
            state.isSaving = false;
          });
        },

        // ----------------------------------------------------------------------
        // Reset
        // ----------------------------------------------------------------------
        
        reset: () => {
          set(() => ({ ...initialState }));
        },
      }))
    ),
    { name: 'firewall-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectProfiles = (state: FirewallState) => state.profiles;
export const selectSelectedProfile = (state: FirewallState & FirewallActions) => 
  state.selectedProfileId ? state.profiles.get(state.selectedProfileId) : undefined;
export const selectDraft = (state: FirewallState) => state.draftProfile;
export const selectDeviceGroups = (state: FirewallState) => state.deviceGroups;
export const selectValidationResult = (state: FirewallState) => state.validationResult;
export const selectTestResults = (state: FirewallState) => state.testResults;
export const selectIsLoading = (state: FirewallState) => state.isLoading;
export const selectError = (state: FirewallState) => state.error;

